import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View, ViewStyle } from 'react-native';
import { Text } from '@/components/Text';
import { useKeyListener } from '@/hooks/useKeyListener';
import { styles } from './styles';

type ClickableLetterSquareProps = {
  currentOrder: number;
  order: number | number[];
  letter: string;
  extraStyle?: ViewStyle;
  id: number;
  onCorrect: (id: number, currentOrder: number, letter: string) => void;
  onWrong: (onWrongParam: number) => void;
  isHidden: boolean;
  onWrongParam: number;
  marginLeft: number;
  size: number;
  wrongBoxChosen: number;
};

export function ClickableLetterSquare({
  currentOrder,
  order,
  letter,
  extraStyle,
  id,
  onCorrect,
  onWrong,
  isHidden,
  onWrongParam,
  marginLeft,
  size,
  wrongBoxChosen,
}: ClickableLetterSquareProps) {
  const [isWrong, setIsWrong] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const responsiveStylesForLetter = StyleSheet.create({
    choiceWrapper: {
      paddingHorizontal: marginLeft,
      paddingVertical: marginLeft,
      marginLeft: marginLeft,
      marginBottom: useResponsiveHeight(1),
      width: size,
      height: size,
    },
    choiceText: {
      fontSize: size / 1.35,
    },
  });

  const onWrongClick = () => {
    setIsWrong(true);
    setTimeout(() => {
      setIsWrong(false);
    }, 500);
    onWrong(onWrongParam);
  };

  const onCorrectClick = () => {
    setIsCorrect(true);
    setShowHint(false);
    onCorrect(id, currentOrder, letter);
  };

  const onClick = () => {
    // Prevent double clicks by checking if we're already processing
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    setTimeout(() => {
      setIsProcessing(false);
    }, 200);
    if (isHidden) {
      return;
    }
    if (order === -1 || !(order as number[]).includes(currentOrder)) {
      return onWrongClick();
    }
    if ((order as number[]).includes(currentOrder)) {
      onCorrectClick();
    }
  };

  useKeyListener({
    correct: (order as number[])?.includes?.(currentOrder) || false,
    letter,
    onCorrect: onCorrectClick,
    onWrong: onWrongClick,
    order: currentOrder,
  });

  useEffect(() => {
    if (wrongBoxChosen >= 2) {
      if (order === -1) {
        return;
      }
      if ((order as number[]).includes(currentOrder)) {
        setShowHint(true);
      }
    }
  }, [wrongBoxChosen, currentOrder]);

  if (isHidden) {
    return (
      <View
        style={[
          styles.container,
          extraStyle,
          styles.hidden,
          responsiveStylesForLetter.choiceWrapper,
        ]}
      >
        <Text
          style={[
            styles.text,
            styles.fontAbz,
            (isWrong || isCorrect) && styles.lightColor,
            responsiveStylesForLetter.choiceText,
          ]}
        >
          {letter}
        </Text>
      </View>
    );
  }

  return (
    <Pressable onPress={onClick}>
      <View
        style={[
          styles.container,
          extraStyle,
          isWrong && styles.wrongBg,
          (showHint || isCorrect) && styles.correctBg,
          responsiveStylesForLetter.choiceWrapper,
        ]}
      >
        <Text
          style={[
            styles.text,
            styles.fontAbz,
            (showHint || isWrong || isCorrect) && styles.lightColor,
            responsiveStylesForLetter.choiceText,
          ]}
        >
          {letter}
        </Text>
      </View>
    </Pressable>
  );
}
